import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DeliveryWindowTable } from '@geofences/delivery-windows/delivery-window-table/delivery-window-table';
import { DeliveryWindowsHeader } from '@geofences/delivery-windows/delivery-windows-header/delivery-windows-header';
import { GeoCoordinate, GeofenceKind, GeofenceV1Request, GeofenceV1Response, SlimGeofence } from '@geofences/geofence';
import { GeofenceDetailsHeader } from '@geofences/geofence-details-header/geofence-details-header';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { firstValueFrom } from 'rxjs';

import { BaseCard } from '@core/components/base-card/base-card';
import {
  GeofenceCoordinates,
  GoogleMapsPolygonEditorComponent
} from '@core/components/google-maps-polygon-editor/google-maps-polygon-editor';
import { GeofenceService } from '@core/services/geofence';

function coordinatesEqual(a: GeoCoordinate[], b: GeoCoordinate[]): boolean {
  return a.length === b.length && a.every((c, i) => c.latitude === b[i].latitude && c.longitude === b[i].longitude);
}

@Component({
  selector: 'app-edit-geofence-form',
  standalone: true,
  templateUrl: './edit-geofence-form.html',
  styleUrl: './edit-geofence-form.scss',
  imports: [
    GoogleMapsPolygonEditorComponent,
    ButtonModule,
    SelectButton,
    FormsModule,
    BaseCard,
    DeliveryWindowTable,
    DeliveryWindowsHeader,
    GeofenceDetailsHeader
  ]
})
export class EditGeofenceForm implements OnInit {
  private readonly geofenceApis = inject(GeofenceService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  readonly id = input.required<string>();

  protected readonly loading = signal<boolean>(false);
  protected readonly saving = signal<boolean>(false);
  protected readonly hubGeofence = signal<GeofenceV1Response | null>(null);
  private readonly geofences = signal<GeofenceV1Response[]>([]);
  private readonly pristineGeofences = signal<GeofenceV1Response[]>([]);
  protected readonly selectedGeofence = signal<SlimGeofence | null>(null);

  protected readonly hubLatLng = computed(() => {
    const hubGeofence = this.hubGeofence();
    return hubGeofence ? this.getCenterLatLng(hubGeofence.coordinates) : null;
  });

  protected readonly slimGeofences = computed(() => {
    return this.geofences()
      .map<SlimGeofence>((g) => ({
        name: g.name,
        geofenceId: g.geofenceId,
        geofenceKind: g.geofenceKindId,
        coordinates: g.coordinates
      }))
      .sort((a, b) => a.geofenceKind - b.geofenceKind);
  });

  protected readonly geofenceOptions = computed(() =>
    this.slimGeofences().map((g) => ({
      label: GeofenceKind[g.geofenceKind],
      value: g.geofenceId,
      kind: g.geofenceKind
    }))
  );

  protected readonly selectedGeofenceId = computed(() => this.selectedGeofence()?.geofenceId ?? null);

  protected readonly dirtyGeofences = computed(() => {
    const pristineById = new Map(this.pristineGeofences().map((g) => [g.geofenceId, g]));
    return this.geofences().filter((g) => {
      const pristine = pristineById.get(g.geofenceId);
      return !pristine || !coordinatesEqual(pristine.coordinates, g.coordinates);
    });
  });

  protected readonly hasChanges = computed(() => this.dirtyGeofences().length > 0);

  protected readonly GeofenceKind = GeofenceKind;

  ngOnInit(): void {
    const id = this.id();
    if (id) {
      this.loadGeofence(id);
      this.loadIntersectingGeofences(id);
    }
  }

  private async loadGeofence(id: string) {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(this.geofenceApis.getById(id));
      const hubGeofence = response.data;
      this.hubGeofence.set(hubGeofence);
      this.selectedGeofence.set({
        name: hubGeofence.name,
        geofenceId: hubGeofence.geofenceId,
        geofenceKind: hubGeofence.geofenceKindId,
        coordinates: hubGeofence.coordinates
      });
    } catch (error) {
      console.error('Failed to load geofence:', error);
      this.showError('Failed to load geofence details');
      this.router.navigate(['geofences']);
    } finally {
      this.loading.set(false);
    }
  }

  private async loadIntersectingGeofences(id: string) {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(this.geofenceApis.getIntersectingGeofences(id));
      this.geofences.set(response.data);
      this.pristineGeofences.set(response.data);
    } catch (error) {
      console.error('Failed to load intersecting geofences:', error);
      this.showError('Failed to load related geofences');
      this.router.navigate(['geofences']);
    } finally {
      this.loading.set(false);
    }
  }

  onSelectGeofence(geofenceId: string): void {
    const geofence = this.slimGeofences().find((g) => g.geofenceId === geofenceId) ?? null;
    this.selectedGeofence.set(geofence);
  }

  onGeofenceCoordinatesChange({ geofence, coordinates }: GeofenceCoordinates): void {
    // Edits without a geofence come from drawing new polygons, which this page doesn't support
    if (!geofence) return;

    this.geofences.update((list) =>
      list.map((g) => (g.geofenceId === geofence.geofenceId ? { ...g, coordinates } : g))
    );
  }

  async onSave(): Promise<void> {
    const dirty = this.dirtyGeofences();
    if (!dirty.length || this.saving()) return;

    this.saving.set(true);

    try {
      const responses = await Promise.all(
        dirty.map((g) => {
          const payload: GeofenceV1Request = {
            name: g.name,
            coordinates: g.coordinates,
            geofenceKindId: g.geofenceKindId
          };
          return firstValueFrom(this.geofenceApis.update(g.geofenceId, payload));
        })
      );

      // Merge saved responses so local state carries the server's canonical coordinates and eTags
      const savedById = new Map(responses.map((r) => [r.data.geofenceId, r.data]));
      this.geofences.update((list) => list.map((g) => savedById.get(g.geofenceId) ?? g));
      this.pristineGeofences.set(this.geofences());

      const hub = this.hubGeofence();
      const savedHub = hub && savedById.get(hub.geofenceId);
      if (savedHub) {
        this.hubGeofence.set(savedHub);
      }

      this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Geofence changes saved' });
    } catch (error: any) {
      const errorMessage = error?.error?.message ?? error?.error ?? 'Failed to save geofence changes';
      this.showError(typeof errorMessage === 'string' ? errorMessage : 'Failed to save geofence changes');
    } finally {
      this.saving.set(false);
    }
  }

  onDiscard(): void {
    this.geofences.set(this.pristineGeofences());
  }

  getCenterLatLng(coordinates: GeoCoordinate[]): GeoCoordinate {
    const { latitude, longitude } = coordinates.reduce(
      (acc, c) => ({
        latitude: acc.latitude + c.latitude,
        longitude: acc.longitude + c.longitude
      }),
      { latitude: 0, longitude: 0 }
    );

    return {
      latitude: latitude / coordinates.length,
      longitude: longitude / coordinates.length
    };
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail });
  }
}
